import {
  Avatar,
  Box,
  Button,
  Divider,
  Group,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import DOMPurify from "dompurify";
import React, { useRef, useState } from "react";
import { IssueComment, User } from "types";
import { FormValues } from "./types";
import { useUser } from "hooks/useUser";
import { useCreateComment } from "api/comments/useCreateComment";
import { useCommentsByIssue } from "api/comments/useCommentsByIssue";
import { getInitials } from "utils/getInitials";
import { RawTiptapEditor } from "features/kanban-board/components/IssueModal/richtexteditor";
import { useGetIssueHistory } from "api/issues/useGetIssueHistory";
import { useCreateMentionNotification } from "api/comments/mention";
import { PermissionButton } from "api/permission/buttonpermission";

type IssueFormProps = {
  selectedIssue: string;
  projectId: string;
  members: User<string>[];
  // userIds: string[];
};
const getStringValue = (value: unknown): string =>
  typeof value === "string" ? value : "";

const getTimeAgo = (timestamp: string) => {
  const now = new Date();
  const past = new Date(timestamp);
  const diff = now.getTime() - past.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return "Just now";
};

// Helper component for a single comment, typed explicitly
function CommentItem({ comment }: { comment: IssueComment }) {
  const firstName: string = getStringValue(comment.createdBy?.firstName) || "N";
  const lastName: string = getStringValue(comment.createdBy?.lastName) || "A";
  const displayName = `${
    getStringValue(comment.createdBy?.firstName) || "Unknown"
  } ${getStringValue(comment.createdBy?.lastName) || "User"}`;
  return (
    <Group align="flex-start" key={comment._id} noWrap>
      <Avatar>{getInitials(firstName, lastName)}</Avatar>
      <Box style={{ flex: 1 }}>
        <Group position="apart">
          <Text fw={600}>{displayName}</Text>
          <Text size="xs" c="dimmed">
            {getTimeAgo(comment.createdAt)}
          </Text>
        </Group>
        <Text
          size="sm"
          mt={4}
          component="div"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(comment.content),
          }}
        />
      </Box>
    </Group>
  );
}

function CommentsTab({
  selectedIssue,
  projectId,
  members,
}: // userIds,
IssueFormProps) {
  const { user } = useUser();
  const { mutate: createComment, isLoading } = useCreateComment(selectedIssue);
  const { data } = useCommentsByIssue(selectedIssue);
  const { mutate: notifyMentions } = useCreateMentionNotification();
  const [clearSignal, setClearSignal] = useState(0);

  const extractMentionIds = (html: string): string[] => {
    const regex = /class="mention"[^>]*data-id="([^"]+)"/g;
    const ids = new Set<string>();

    // explicitly type m so it's not `any`
    let m: RegExpExecArray | null = regex.exec(html);
    while (m !== null) {
      ids.add(m[1]);
      m = regex.exec(html);
    }

    return Array.from(ids);
  };

  const comments: IssueComment[] = data ?? [];

  const form = useForm<FormValues>({
    initialValues: {
      issueId: selectedIssue,
      content: "",
      createdBy: user?._id ?? "",
    },
    validate: {
      content: (value) =>
        value.trim().length === 0 ? "Comment cannot be empty" : null,
      createdBy: (value) =>
        value && !value.trim() ? "Created by is required" : null,
      issueId: (value) => (!value ? "Issue ID is required" : null),
    },
  });

  // const handleSubmit = (values: FormValues) => {
  //   const mentionIds = extractMentionIds(values.content);

  //   createComment(values, {
  //     onSuccess: () => {
  //       form.reset();
  //     },
  //   });
  // };

  const handleSubmit = (values: FormValues) => {
    const mentionIds = extractMentionIds(values.content);
    createComment(values, {
      onSuccess: () => {
        form.reset();
        setClearSignal((s) => s + 1);
        if (mentionIds.length > 0) {
          notifyMentions({
            issueId: values.issueId,
            mentions: mentionIds,
            mentionedBy: user?._id,
          });
        }
      },
    });
  };

  return (
    <Box p="md">
      <Stack spacing="md">
        <ScrollArea style={{ height: 210 }} offsetScrollbars>
          {comments.map((comment: IssueComment) => (
            <CommentItem key={comment._id} comment={comment} />
          ))}
        </ScrollArea>

        {comments && comments.length !== 0 && <Divider />}

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Group noWrap>
            {/* <Textarea
              placeholder="Add a comment..."
              style={{ flex: 1 }}
              {...form.getInputProps("content")}
              minRows={1}
              autosize
            /> */}
            <RawTiptapEditor
              user={user._id}
              members={members}
              projectId={projectId}
              issueId={selectedIssue}
              clearSignal={clearSignal}
              onChange={(html: string) => form.setFieldValue("content", html)}
            />

            <PermissionButton
              // type="submit"
              permission="task_management.mention"
              type="submit"
              variant="filled"
              loading={isLoading}
              // disabled={!form.isDirty()}
            >
              Post
            </PermissionButton>
            {/* <Button
              type="submit"
              color="dark"
              loading={isLoading}
              disabled={!form.isDirty()}
            >
              Post
            </Button> */}
          </Group>
        </form>
      </Stack>
    </Box>
  );
}

export default CommentsTab;
